import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Wrench } from 'lucide-react'
import { Button } from '~/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '~/components/ui/card'
import { EmptyState } from '~/components/shared/empty-state'
import { VerdictBadge } from '~/components/assessment/assessment-badges'
import { useUiStore } from '~/stores/ui-store'
import { cn } from '~/lib/utils'
import type {
    AssessmentReport,
    AssessmentResponse,
    ChecklistItem,
    ItemScore,
    SurveyAnswer,
} from '~/api/types'
import { itemCategoryPath, resultMethodLabel } from './utils'
import { ReportSummaryTab } from './report-summary-tab'
import { ChecklistResultDetail } from './checklist-result-detail'

type ResultTab = 'summary' | 'scores'

/** scored/reported 상태: 항목별 채점 결과 + 보고서. */
export function ResultsView({
    assessment,
    scores,
    checklist,
    answers,
    report,
    onReport,
    reporting,
    onEdit,
}: {
    assessment: AssessmentResponse
    scores: ItemScore[]
    checklist: ChecklistItem[] | null
    answers: SurveyAnswer[]
    report: AssessmentReport | null
    onReport(): void
    reporting: boolean
    onEdit(): void
}) {
    // itemId → 제목(체크리스트가 있으면 채점 결과에 제목을 붙인다).
    const titleById = new Map<string, ChecklistItem>()
    for (const item of checklist ?? []) titleById.set(item.id, item)
    const navigate = useNavigate()
    // 변경 검토(유지하기)는 숨김 서비스라, 노출된 경우에만 진입 버튼을 보인다.
    const hiddenServicesRevealed = useUiStore((s) => s.hiddenServicesRevealed)
    const [activeTab, setActiveTab] = useState<ResultTab>('summary')
    const [selectedItemId, setSelectedItemId] = useState<string | null>(null)
    const selectedScore =
        scores.find((score) => score.itemId === selectedItemId) ?? scores[0]
    const selectedItem = selectedScore
        ? titleById.get(selectedScore.itemId)
        : undefined

    useEffect(() => {
        if (scores.length === 0) {
            setSelectedItemId(null)
            return
        }
        if (
            !selectedItemId ||
            !scores.some((s) => s.itemId === selectedItemId)
        ) {
            setSelectedItemId(scores[0].itemId)
        }
    }, [scores, selectedItemId])

    return (
        <div className='flex h-full min-h-0 flex-col overflow-hidden'>
            <div
                className='border-border mb-4 flex shrink-0 flex-wrap items-center justify-between gap-3 border-b pb-3'
                role='tablist'
                aria-label='평가 결과 보기'>
                <div className='flex flex-wrap gap-2'>
                    <button
                        type='button'
                        role='tab'
                        aria-selected={activeTab === 'summary'}
                        onClick={() => setActiveTab('summary')}
                        className={cn(
                            'rounded-sm border px-4 py-2 text-sm font-medium',
                            activeTab === 'summary'
                                ? 'border-primary bg-accent text-primary'
                                : 'border-border bg-card text-foreground hover:border-primary'
                        )}>
                        총 결과
                    </button>
                    <button
                        type='button'
                        role='tab'
                        aria-selected={activeTab === 'scores'}
                        onClick={() => setActiveTab('scores')}
                        className={cn(
                            'rounded-sm border px-4 py-2 text-sm font-medium',
                            activeTab === 'scores'
                                ? 'border-primary bg-accent text-primary'
                                : 'border-border bg-card text-foreground hover:border-primary'
                        )}>
                        채점 결과
                    </button>
                </div>
                <div className='flex items-center gap-2'>
                    {hiddenServicesRevealed ? (
                        <Button
                            variant='outline'
                            onClick={() =>
                                navigate(
                                    `/maintenance?assessment=${assessment.id}`
                                )
                            }
                            title='이 평가로 변경 영향 분석을 시작해요'>
                            <Wrench className='size-4' />
                            변경 검토
                        </Button>
                    ) : null}
                    <Button variant='outline' onClick={onEdit}>
                        평가 수정하기
                    </Button>
                    <Button
                        variant={report ? 'default' : 'secondary'}
                        onClick={
                            report
                                ? () =>
                                      window.open(
                                          `/certification/${assessment.id}/report`,
                                          '_blank'
                                      )
                                : onReport
                        }
                        disabled={reporting}>
                        {reporting
                            ? '생성 중…'
                            : report
                              ? '보고서 다운로드'
                              : '보고서 생성하기'}
                    </Button>
                </div>
            </div>

            {activeTab === 'summary' ? (
                <section
                    className='min-h-0 flex-1 overflow-y-auto'
                    aria-label='총 결과'
                    data-print-report>
                    <ReportSummaryTab
                        assessment={assessment}
                        report={report}
                        scores={scores}
                        checklist={checklist}
                        answers={answers}
                        onReport={onReport}
                        reporting={reporting}
                    />
                </section>
            ) : (
                <section
                    className='grid min-h-0 flex-1 gap-4 overflow-y-auto xl:grid-cols-[minmax(0,1.15fr)_minmax(360px,0.85fr)] xl:overflow-hidden'
                    aria-label='채점 결과'>
                    <div className='min-h-0 xl:overflow-y-auto xl:pr-1'>
                        {scores.length === 0 ? (
                            <EmptyState
                                title='채점 결과가 없어요'
                                description='채점이 완료되면 항목별 결과가 표시돼요.'
                            />
                        ) : (
                            <ul className='space-y-2'>
                                {scores.map((s) => {
                                    const item = titleById.get(s.itemId)
                                    const selected =
                                        selectedScore?.itemId === s.itemId
                                    return (
                                        <li key={s.itemId}>
                                            <button
                                                type='button'
                                                aria-pressed={selected}
                                                onClick={() =>
                                                    setSelectedItemId(s.itemId)
                                                }
                                                className={cn(
                                                    'flex w-full items-start justify-between gap-3 rounded-md border p-3 text-left transition-colors',
                                                    selected
                                                        ? 'border-primary bg-accent'
                                                        : 'border-border bg-card hover:border-primary hover:bg-accent/50'
                                                )}>
                                                <div className='min-w-0'>
                                                    <p className='text-foreground text-sm font-medium'>
                                                        {item?.title ??
                                                            s.itemId}
                                                    </p>
                                                    <p className='text-muted-foreground mt-1 text-xs'>
                                                        {item
                                                            ? itemCategoryPath(
                                                                  item
                                                              )
                                                            : '체크리스트 상세 없음'}
                                                    </p>
                                                    <p className='text-muted-foreground mt-1 text-xs'>
                                                        점수 {s.score ?? 0}/
                                                        {s.maxScore} ·{' '}
                                                        {resultMethodLabel(
                                                            s.method
                                                        )}
                                                    </p>
                                                </div>
                                                {s.verdict ? (
                                                    <div className='shrink-0'>
                                                        <VerdictBadge
                                                            verdict={s.verdict}
                                                        />
                                                    </div>
                                                ) : null}
                                            </button>
                                        </li>
                                    )
                                })}
                            </ul>
                        )}
                    </div>

                    <aside className='min-h-0 xl:overflow-y-auto'>
                        <Card>
                            <CardHeader>
                                <CardTitle>선택 항목 상세</CardTitle>
                            </CardHeader>
                            <CardContent>
                                {selectedScore && selectedItem ? (
                                    <ChecklistResultDetail
                                        item={selectedItem}
                                        score={selectedScore}
                                        answers={answers}
                                    />
                                ) : (
                                    <p className='text-muted-foreground text-sm'>
                                        왼쪽에서 결과 항목을 선택하세요.
                                    </p>
                                )}
                            </CardContent>
                        </Card>
                    </aside>
                </section>
            )}
        </div>
    )
}
